'use client'

// useEffectivePlan — the COMP-AWARE plan for client components.
//
// session.user.plan is the raw Stripe tier (comp-blind): a comp-granted PRO
// tester reads as FREE, which hid paid affordances ("Another similar question")
// and showed FREE upsells (the drill cap-upgrade screen) to comped users.
//
// The hook starts from the session plan — a safe OPTIMISTIC LOWER BOUND, since
// a comp grant can only ever RAISE the effective plan above the Stripe tier —
// and corrects upward once /api/me/plan (server-side getEffectivePlan) answers.
// Fetch failures keep the session plan, so gating degrades to exactly the old
// (comp-blind) behavior, never to something more permissive than the server.

import { useEffect, useState } from 'react'
import type { PlanKey } from '@/lib/billing/plan-config'
import { coercePlan } from '@/lib/billing/effective-plan'

export function useEffectivePlan(sessionPlan: string | null | undefined): PlanKey {
  const base = coercePlan(sessionPlan ?? 'FREE')
  const [plan, setPlan] = useState<PlanKey>(base)

  useEffect(() => {
    // Already at the top tier — nothing a comp could add.
    if (base === 'PRO') {
      setPlan('PRO')
      return
    }
    setPlan(base)
    let cancelled = false
    fetch('/api/me/plan', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { plan?: string } | null) => {
        if (!cancelled && data?.plan) setPlan(coercePlan(data.plan))
      })
      .catch(() => {
        /* best-effort — session plan stays in effect */
      })
    return () => {
      cancelled = true
    }
  }, [base])

  return plan
}
